import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import type { DateRange } from "../utils/date";
import { DashboardService } from "../../../../services/dashboard/dashboardService";
import { ProviderStatCard } from "../../components/providers/ProviderStatCard";
import { ProviderDonut } from "../../components/providers/ProviderDonut";
import { FaUsers, FaBuilding, FaUserTie, FaHome, FaCheckCircle, FaTimesCircle, FaFileDownload, FaExclamationCircle, FaExclamationTriangle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const fadeUp = {
    hidden: { opacity: 0, y: 12 },
    visible: (i = 1) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06 } }),
};

interface Props {
    range: DateRange;
    refreshKey: number;
}

export const ProvidersStatsTab: React.FC<Props> = ({ range, refreshKey }) => {
    const [stats, setStats] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchStats = async () => {
        try {
            setLoading(true);
            setError(null);
            const service = new DashboardService();
            const res = await service.providerStatistics();
            const body = (res as any)?.response?.data;
            if (body?.success) setStats(body.data ?? null);
            else setStats(null);
        } catch (e: any) {
            setError(e?.message || "Error obteniendo estadísticas de proveedores");
            setStats(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range.start, range.end, refreshKey]);

    const typeData = stats?.providersByType?.map((t: any) => ({
        id: t.id,
        name: t.name || "Sin tipo",
        value: parseInt(t.count) || 0,
    })) || [];

    const activeCount = parseInt(stats?.providersByStatus?.find((s: any) => s.status === 'activo')?.count) || 0;
    const closedCount = parseInt(stats?.providersByStatus?.find((s: any) => s.status === 'cerrado')?.count) || 0;
    const retiredCount = parseInt(stats?.providersByStatus?.find((s: any) => s.status === 'retirado')?.count) || 0;
    const quarantineCount = parseInt(stats?.providersByStatus?.find((s: any) => s.status === 'cuarentena')?.count) || 0;

    const statusData = [
        { name: "Activos", value: activeCount },
        { name: "Cerrados", value: closedCount },
        { name: "Retirados", value: retiredCount },
        { name: "En Cuarentena", value: quarantineCount },
    ];

    const totalActive = activeCount;
    const totalClosed = closedCount;
    const totalRetired = retiredCount;
    const totalQuarantine = quarantineCount;

    const navigate = useNavigate();

    const handleCardClick = (status: string) => {
        const filter = {
            filter_filters: JSON.stringify({ status }),
            filter_order: JSON.stringify([]),
            page: "1",
            limit: "50",
        };
        const queryParams = new URLSearchParams(filter).toString();
        navigate(`/providers?${queryParams}`);
    };

    const handleTypeClick = (providerTypeId?: string) => {
        if (!providerTypeId) return;
    
        const filter = {
            filter_filters: JSON.stringify({ provider_type: providerTypeId }),
            filter_order: JSON.stringify([]),
            page: "1",
            limit: "50",
        };
    
        const queryParams = new URLSearchParams(filter).toString();
        navigate(`/providers?${queryParams}`);
    };

    return (
        <div className="space-y-6">
            {/* Header con Exportar */}
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">Resumen de Proveedores</h3>
                <button
                    onClick={() => alert("Próximamente: exportación de datos de proveedores")}
                    className="inline-flex items-center gap-2 rounded-xl bg-white border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition-colors"
                >
                    <FaFileDownload className="h-4 w-4 text-indigo-500" />
                    Exportar Listado
                </button>
            </div>

            {/* Fila de Tarjetas de Resumen */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
                    <ProviderStatCard
                        title="Total Proveedores"
                        value={stats?.totalProviders || 0}
                        loading={loading}
                        error={error}
                        icon={<FaUsers className="h-5 w-5" />}
                    />
                </motion.div>
                <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={1}>
                    <ProviderStatCard
                        title="Proveedores Activos"
                        value={totalActive}
                        loading={loading}
                        error={error}
                        className="border-l-4 border-l-emerald-500"
                        icon={<FaCheckCircle className="h-5 w-5 text-emerald-500" />}
                        onClick={() => handleCardClick("1")}
                    />
                </motion.div>
                <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={2}>
                    <ProviderStatCard
                        title="Proveedores Cerrados"
                        value={totalClosed}
                        loading={loading}
                        error={error}
                        className="border-l-4 border-l-grey-500"
                        icon={<FaTimesCircle className="h-5 w-5 text-grey-500" />}
                        onClick={() => handleCardClick("2")}
                    />
                </motion.div>
                <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={3}>
                    <ProviderStatCard
                        title="Proveedores Retirados"
                        value={totalRetired}
                        loading={loading}
                        error={error}
                        className="border-l-4 border-l-rose-500"
                        icon={<FaExclamationCircle className="h-5 w-5 text-rose-500" />}
                        onClick={() => handleCardClick("3")}
                    />
                </motion.div>
                <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={4}>
                    <ProviderStatCard
                        title="Proveedores en Cuarentena"
                        value={totalQuarantine}
                        loading={loading}
                        error={error}
                        className="border-l-4 border-l-yellow-500"
                        icon={<FaExclamationTriangle className="h-5 w-5 text-yellow-500" />}
                        onClick={() => handleCardClick("4")}
                    />
                </motion.div>
            </div>

            {/* Gráficos de Distribución */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={3}>
                    <ProviderDonut
                        title="Distribución por Tipo"
                        periodLabel={`Actualizado: ${new Date().toLocaleDateString()}`}
                        data={typeData}
                        loading={loading}
                        error={error}
                        colors={["#6366f1", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899"]}
                        onSectorClick={(item) => handleTypeClick(item?.id)}
                    />
                </motion.div>
                <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={4}>
                    <ProviderDonut
                        title="Estado de Proveedores"
                        periodLabel={`Actualizado: ${new Date().toLocaleDateString()}`}
                        data={statusData}
                        loading={loading}
                        error={error}
                        colors={["#10b981", "#6c757d", "#ec4899", "#f59e0b"]}
                    />
                </motion.div>
            </div>
        </div>
    );
};
